import { getCurrentScale, getDimensionScale } from './get-scale.js';
import { isRectDatapoint, isRectSeries } from './type-guards.js';
import { relAccessor } from './value-representation.js';
import type { Point } from '../../core/types/point.js';
import { safeGet } from '../../core/utils/safe-get.js';
import {
  xAccessor as barXAccessor,
  yAccessor as barYAccessor,
  widthAccessor as barWidthAccessor,
  heightAccessor as barHeightAccessor,
} from '../components/displays/bar-accessors.js';
import {
  xAccessor as rectXAccessor,
  yAccessor as rectYAccessor,
  widthAccessor as rectWidthAccessor,
  heightAccessor as rectHeightAccessor,
} from '../components/displays/rect-accessors.js';
import type {
  BarSeriesQueryResult,
  RectSeriesQueryResult,
} from '../context/XYChartOverlayPerformance.context.js';
import type { AxisIdToScales } from '../context/XYChartScales.context.js';
import {
  RECT_VARIANT,
  type AxisIdToScaleDomain,
  type BarSeriesWithDatapoints,
  type RectDatapointInternal,
  type RectSeriesWithDatapoints,
  type SeriesToMetadata,
  type MapDatapoints,
} from '../types/xy-chart-internals.js';
import type { BarDatapoint } from '../types/xy-chart.js';

/**
 * Check if a pixel position is within the defined range.
 */
function isInPixelRange(
  position: number,
  start: number,
  span: number,
): boolean {
  return position >= start && position <= start + span;
}

interface RectAccessors {
  xAccessor: (datapoint: RectDatapointInternal) => number;
  yAccessor: (datapoint: RectDatapointInternal) => number;
  widthAccessor: (datapoint: RectDatapointInternal) => number;
  heightAccessor: (datapoint: RectDatapointInternal) => number;
}
interface BarAccessors {
  xAccessor: (datapoint: BarDatapoint) => number;
  yAccessor: (datapoint: BarDatapoint) => number;
  widthAccessor: (datapoint: BarDatapoint) => number;
  heightAccessor: (datapoint: BarDatapoint) => number;
}

function getAccessors(
  series: RectSeriesWithDatapoints | BarSeriesWithDatapoints,
  axisIdToScales: AxisIdToScales,
  datapointMetadataMap: MapDatapoints,
  xRelAccessor?: (d: number) => number,
  yRelAccessor?: (d: number) => number,
) {
  const { type, xAxisId, yAxisId } = series;

  const xScale = getCurrentScale(axisIdToScales, xAxisId, type);
  const yScale = getCurrentScale(axisIdToScales, yAxisId, type);

  if (type === RECT_VARIANT) {
    return {
      xAccessor: rectXAccessor(xScale, xRelAccessor),
      yAccessor: rectYAccessor(yScale, yRelAccessor),
      widthAccessor: rectWidthAccessor(xScale, xRelAccessor),
      heightAccessor: rectHeightAccessor(yScale, yRelAccessor),
    };
  }

  // type === BAR_VARIANT
  const dimensionXScale = getDimensionScale(axisIdToScales, xAxisId);
  const dimensionYScale = getDimensionScale(axisIdToScales, yAxisId);
  return {
    xAccessor: barXAccessor(
      xScale,
      datapointMetadataMap,
      dimensionXScale,
      series.id,
      xRelAccessor,
    ),
    yAccessor: barYAccessor(
      yScale,
      datapointMetadataMap,
      dimensionYScale,
      series.id,
      yRelAccessor,
    ),
    widthAccessor: barWidthAccessor(
      xScale,
      datapointMetadataMap,
      dimensionXScale,
      xRelAccessor,
    ),
    heightAccessor: barHeightAccessor(
      yScale,
      datapointMetadataMap,
      dimensionYScale,
      yRelAccessor,
    ),
  };
}

export const getPixelDimensionsForDatapoint = (
  datapoint: RectDatapointInternal | BarDatapoint,
  genAccessors: RectAccessors | BarAccessors,
): { x: number; y: number; width: number; height: number } => {
  if (isRectDatapoint(datapoint)) {
    const accessors = genAccessors as RectAccessors;
    return {
      x: accessors.xAccessor(datapoint),
      y: accessors.yAccessor(datapoint),
      width: accessors.widthAccessor(datapoint),
      height: accessors.heightAccessor(datapoint),
    };
  }
  const accessors = genAccessors as BarAccessors;
  return {
    x: accessors.xAccessor(datapoint),
    y: accessors.yAccessor(datapoint),
    width: accessors.widthAccessor(datapoint),
    height: accessors.heightAccessor(datapoint),
  };
};

/**
 * Retrieves the datapoint that is currently being hovered over based on the given position.
 */
export function getHoveredDatapoint(
  position: Point,
  series: RectSeriesWithDatapoints | BarSeriesWithDatapoints,
  axisIdToScales: AxisIdToScales,
  datapointMetadataMap: MapDatapoints,
  seriesToMetadata: SeriesToMetadata,
  axisIdToScaleDomain: AxisIdToScaleDomain,
) {
  const data = isRectSeries(series) ? series.filteredData : series.data;

  if (position.x < 0 || position.y < 0) {
    return undefined;
  }

  const { xAxis, yAxis } = safeGet(seriesToMetadata, series);
  const xRelAccessor = relAccessor(xAxis, axisIdToScaleDomain);
  const yRelAccessor = relAccessor(yAxis, axisIdToScaleDomain);

  const accessors = getAccessors(
    series,
    axisIdToScales,
    datapointMetadataMap,
    xRelAccessor,
    yRelAccessor,
  );

  for (const datapoint of data) {
    if (safeGet(datapointMetadataMap, datapoint).isGap) {
      continue;
    }

    const { x, y, width, height } = getPixelDimensionsForDatapoint(
      datapoint,
      accessors,
    );

    if (
      isInPixelRange(position.x, x, width) &&
      isInPixelRange(position.y, y, height)
    ) {
      return datapoint;
    }
  }

  return undefined;
}

export function buildQueryHovered(
  axisIdToScales: AxisIdToScales,
  datapointMetadataMap: MapDatapoints,
  seriesToMetadata: SeriesToMetadata,
  axisIdToScaleDomain: AxisIdToScaleDomain,
) {
  function query(
    arrayOfSeries: RectSeriesWithDatapoints[],
    position: Point,
  ): RectSeriesQueryResult | undefined;
  function query(
    arrayOfSeries: BarSeriesWithDatapoints[],
    position: Point,
  ): BarSeriesQueryResult | undefined;
  function query(
    arrayOfSeries: (RectSeriesWithDatapoints | BarSeriesWithDatapoints)[],
    position: Point,
  ) {
    for (let i = arrayOfSeries.length - 1; i >= 0; i--) {
      const series = arrayOfSeries[i];
      const datapoint = getHoveredDatapoint(
        position,
        series,
        axisIdToScales,
        datapointMetadataMap,
        seriesToMetadata,
        axisIdToScaleDomain,
      );

      if (!datapoint) {
        continue;
      }

      return {
        type: series.type,
        distance: 0,
        datapoint,
        series,
      };
    }
    return undefined;
  }
  return query;
}
